import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Building2,
  Clock,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Calendar,
} from "lucide-react";

const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const SetupWizard = () => {
  const navigate = useNavigate();
  const { api, refreshBusiness, user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.registered_business_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    description: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    business_hours: DAYS.map((_, i) => ({
      day: i,
      open_time: "09:00",
      close_time: "18:00",
      is_closed: i >= 5,
    })),
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateHours = (dayIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      business_hours: prev.business_hours.map((h, i) =>
        i === dayIndex ? { ...h, [field]: value } : h,
      ),
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.name.trim()) {
        toast.error("Please enter your business name");
        return;
      }
      if (!formData.email.trim()) {
        toast.error("Please enter a contact email");
        return;
      }
    }
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post("/businesses", formData);
      await refreshBusiness();
      toast.success("Business created successfully!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Setup error:", error);
      toast.error(error.response?.data?.detail || "Failed to create business");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: "Business Info", icon: Building2 },
    { num: 2, title: "Hours", icon: Clock },
    { num: 3, title: "Complete", icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Calendar className="w-5 h-5 text-white" />
            </div>

            <span className="font-heading font-bold text-lg sm:text-xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 bg-clip-text text-transparent">
              SlotBook
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Progress Steps */}
        <div className="flex items-center justify-between sm:justify-center gap-2 sm:gap-4 mb-10 sm:mb-12">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center flex-1 sm:flex-none">
                <div
                  className={`wizard-step ${
                    step > s.num
                      ? "completed"
                      : step === s.num
                        ? "active"
                        : "pending"
                  }`}
                >
                  {step > s.num ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <s.icon className="w-5 h-5" />
                  )}
                </div>
                <span className="text-[10px] sm:text-xs mt-2 text-muted-foreground text-center">
                  {s.title}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-1 sm:w-16 ${
                    step > s.num ? "bg-accent" : "bg-border"
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Business Info */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mb-2">
              Tell us about your business
            </h1>
            <p className="text-muted-foreground mb-8">
              This information will appear on your public booking page.
            </p>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g. Sunrise Hair Studio"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  data-testid="business-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Contact Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@business.com"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    data-testid="business-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    data-testid="business-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  placeholder="123 Main St, City, State"
                  value={formData.address}
                  onChange={(e) => updateField("address", e.target.value)}
                  data-testid="business-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Tell customers what makes your business special..."
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  rows={3}
                  data-testid="business-description-input"
                />
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <Button
                onClick={handleNext}
                className="w-full sm:w-auto"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Business Hours */}
        {step === 2 && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mb-2">
              Set your business hours
            </h1>
            <p className="text-muted-foreground mb-8">
              Customers can only book during these hours.
            </p>

            <div className="space-y-3">
              {DAYS.map((day, i) => (
                <div
                  key={day}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 rounded-lg border border-border"
                >
                  <div className="sm:w-28">
                    <span className="font-medium">{day}</span>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!formData.business_hours[i].is_closed}
                      onChange={(e) =>
                        updateHours(i, "is_closed", !e.target.checked)
                      }
                      className="rounded border-border"
                      data-testid={`day-${i}-toggle`}
                    />
                    <span className="text-sm text-muted-foreground">Open</span>
                  </label>

                  {!formData.business_hours[i].is_closed && (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:ml-auto w-full sm:w-auto">
                      <Input
                        type="time"
                        value={formData.business_hours[i].open_time}
                        onChange={(e) =>
                          updateHours(i, "open_time", e.target.value)
                        }
                        className="w-full sm:w-32"
                        data-testid={`day-${i}-open`}
                      />
                      <span className="text-muted-foreground hidden sm:block">
                        to
                      </span>
                      <Input
                        type="time"
                        value={formData.business_hours[i].close_time}
                        onChange={(e) =>
                          updateHours(i, "close_time", e.target.value)
                        }
                        className="w-full sm:w-32"
                        data-testid={`day-${i}-close`}
                      />
                    </div>
                  )}

                  {formData.business_hours[i].is_closed && (
                    <span className="sm:ml-auto text-sm text-muted-foreground">
                      Closed
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:justify-between mt-8">
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="w-full sm:w-auto"
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Complete */}
        {step === 3 && (
          <div className="animate-fade-in text-center">
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-accent" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mb-2">
              You're all set!
            </h1>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Your business profile is ready. Next, you can add services and
              staff members from your dashboard.
            </p>

            <div className="card-base max-w-sm mx-auto mb-8 text-left">
              <h3 className="font-heading font-bold mb-3">Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Business</span>
                  <span className="font-medium">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email</span>
                  <span className="font-medium">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Open Days</span>
                  <span className="font-medium">
                    {formData.business_hours.filter((h) => !h.is_closed).length}{" "}
                    days/week
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full sm:w-auto"
                data-testid="complete-setup-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetupWizard;
